import React, { useRef, useState } from 'react';
import { Button, Form, Input, Modal, Tag, Card, Switch, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import ReCAPTCHA from 'react-google-recaptcha';
import config from "../../config";
import { api } from '../../api/api';
import styles from './adPasswordReset.module.scss';

const ADPasswordReset = () => {
	const [form] = Form.useForm();
	const recaptchaRef = useRef(null);

	const [loadingUser, setLoadingUser] = useState(false);
	const [resetting, setResetting] = useState(false);
	const [userInfo, setUserInfo] = useState(null);
	const [searched, setSearched] = useState(false);
	const [captchaToken, setCaptchaToken] = useState('');
	const [captchaError, setCaptchaError] = useState('');
	const [passwordTemplate, setPasswordTemplate] = useState('');
	const RECAPTCHA_SITE_KEY = config.RECAPTCHA_SITE_KEY;
	console.log("RECAPTCHA_SITE_KEY:", RECAPTCHA_SITE_KEY);
	const handleSearchUser = async () => {
		try {
			const values = await form.validateFields(['username']);
			const username = values?.username?.trim();

			setLoadingUser(true);
			setUserInfo(null);
			setSearched(false);

			const response = await api.get({
				path: '/ad/user-info',
				params: { username },
				service: 'useronboarding',
			});

			if (response?.status === 200) {
				const adData = response?.data;
				setPasswordTemplate(adData?.passwordTemplate || '');
				setUserInfo(adData);
				setSearched(true);

				form.setFieldsValue({
					defaultPassword: adData?.defaultPassword || '',
				});

				if (adData?.exists) {
					message.success('AD user information fetched successfully');

					if (!adData?.defaultPassword) {
						message.warning('Default password pattern not found for this user OU');
					}
				} else {
					setPasswordTemplate('');
					form.setFieldsValue({
						defaultPassword: '',
					});

					Modal.warning({
						title: 'User Not Found',
						content: adData?.message || 'User not found in AD',
						okText: 'Close',
					});
				}
			} else {
				setPasswordTemplate('');
				form.setFieldsValue({
					defaultPassword: '',
				});

				Modal.error({
					title: 'Fetch Failed',
					content: 'Failed to fetch AD user information',
					okText: 'Close',
				});
			}
		} catch (error) {
			if (error?.errorFields) return;

			const errMsg =
				error?.response?.data?.message ||
				error?.response?.data ||
				error?.message ||
				'Failed to fetch AD user information';
			setPasswordTemplate('');
			setUserInfo(null);
			setSearched(true);

			form.setFieldsValue({
				defaultPassword: '',
			});

			Modal.error({
				title: 'Fetch Failed',
				content: errMsg,
				okText: 'Close',
			});
		} finally {
			setLoadingUser(false);
		}
	};

	const handleCaptchaChange = (token) => {
		setCaptchaToken(token || '');
		setCaptchaError('');
	};

	const handleCaptchaExpired = () => {
		setCaptchaToken('');
		setCaptchaError('reCAPTCHA expired. Please verify again.');
	};

	const handleResetPassword = async (values) => {
		if (!userInfo?.exists) {
			Modal.warning({
				title: 'User Information Required',
				content: 'Please search and confirm the AD user before resetting the password.',
				okText: 'Close',
			});
			return;
		}

		if (!captchaToken) {
			setCaptchaError('Please complete the reCAPTCHA verification.');
			return;
		}

		try {
			setResetting(true);

			const payload = {
				username: values?.username?.trim(),
				defaultPassword: values?.defaultPassword?.trim(),
				unlockAccount: !!values?.unlockAccount,
				recaptchaToken: captchaToken,
			};

			const response = await api.post({
				path: '/ad/reset-password',
				formdata: payload,
				service: 'useronboarding',
			});

			if (response?.status === 200) {
				Modal.success({
					title: 'Password Reset Successful',
					content: response?.data || 'AD password has been reset successfully.',
					okText: 'Close',
				});

				const refreshedResponse = await api.get({
					path: '/ad/user-info',
					params: { username: values?.username?.trim() },
					service: 'useronboarding',
				});

				if (refreshedResponse?.status === 200) {
					const refreshedData = refreshedResponse?.data;
					setUserInfo(refreshedData);

					form.setFieldsValue({
						defaultPassword: refreshedData?.defaultPassword || values?.defaultPassword || '',
					});
				}

				setCaptchaToken('');
				setCaptchaError('');
				recaptchaRef.current?.reset();
			} else {
				Modal.error({
					title: 'Reset Failed',
					content: 'Failed to reset AD password',
					okText: 'Close',
				});
			}
		} catch (error) {
			const errMsg =
				error?.response?.data?.message ||
				error?.response?.data ||
				error?.message ||
				'Failed to reset AD password';

			Modal.error({
				title: 'Reset Failed',
				content: errMsg,
				okText: 'Close',
			});

			setCaptchaToken('');
			recaptchaRef.current?.reset();
		} finally {
			setResetting(false);
		}
	};

	const handleUsernameChange = () => {
		setUserInfo(null);
		setPasswordTemplate('');
		setSearched(false);
		form.setFieldsValue({
			defaultPassword: '',
		});
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.topSection}>
				<div className={styles.left}>
					<p className={styles.title}>AD Password Reset</p>
					<p className={styles.subTitle}>
						Search AD user by username, then reset to default password.
					</p>
				</div>
			</div>

			<div className={styles.formCard}>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleResetPassword}
					className={styles.form}
					initialValues={{
						unlockAccount: true,
					}}
				>
					<div className={styles.formGrid}>
						<Form.Item
							label="Username"
							name="username"
							rules={[
								{
									required: true,
									message: 'Please enter username',
								},
							]}
						>
							<Input
								placeholder="Enter sAMAccountName"
								onChange={handleUsernameChange}
								autoComplete="off"
							/>
						</Form.Item>

						<div className={styles.searchBtnWrap}>
							<Button
								type="primary"
								onClick={handleSearchUser}
								loading={loadingUser}
								className={`${styles.actionBtn} ${styles.searchBtn}`}
							>
								Get AD User Information
							</Button>
						</div>

						<Form.Item
							label="Default Password"
							name="defaultPassword"
							rules={[
								{
									required: true,
									message: 'Please enter default password',
								},
							]}
						>
							<Input.Password
								placeholder="Enter default password"
								autoComplete="new-password"
								iconRender={(visible) =>
									visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
								}
							/>
						</Form.Item>

						<Form.Item
							label="Unlock Account"
							name="unlockAccount"
							valuePropName="checked"
							className={styles.switchItem}
						>
							<Switch />
						</Form.Item>
					</div>

					<div style={{ marginBottom: 16 }}>
						<ReCAPTCHA
							ref={recaptchaRef}
							sitekey={RECAPTCHA_SITE_KEY}
							onChange={handleCaptchaChange}
							onExpired={handleCaptchaExpired}
						/>
						{captchaError && (
							<div style={{ color: '#ff4d4f', marginTop: 8 }}>
								{captchaError}
							</div>
						)}
					</div>
					
					{passwordTemplate && (
						<div className={styles.passwordTemplateWrap}>
							<div
								className={styles.passwordTemplate}
							>{passwordTemplate}</div>
						</div>
					)}

					<div className={styles.formActions}>
						<Button
							type="primary"
							htmlType="submit"
							loading={resetting}
							className={`${styles.actionBtn} ${styles.submitBtn}`}
							disabled={!userInfo?.exists || !captchaToken}
						>
							Reset Password
						</Button>
					</div>
				</Form>
			</div>

			{searched && (
				<Card className={styles.infoCard} title="AD User Information">
					{userInfo?.exists ? (
						<div className={styles.infoGrid}>
							<div className={styles.infoRow}>
								<span className={styles.label}>Username</span>
								<span className={styles.value}>{userInfo?.username || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Display Name</span>
								<span className={styles.value}>{userInfo?.displayName || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Given Name</span>
								<span className={styles.value}>{userInfo?.givenName || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Last Name</span>
								<span className={styles.value}>{userInfo?.sn || '-'}</span>
							</div>
							<div className={styles.infoRow}>
								<span className={styles.label}>Email</span>
								<span className={styles.value}>{userInfo?.email || '-'}</span>
							</div>
							<div className={styles.infoRow}>
								<span className={styles.label}>OU</span>
								<span className={styles.value}>{userInfo?.ou || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Description</span>
								<span className={styles.value}>{userInfo?.description || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Bad Password Count</span>
								<span className={styles.value}>{userInfo?.badPwdCount || '0'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Last Bad Password Time</span>
								<span className={styles.value}>{userInfo?.badPasswordTime || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Created Date</span>
								<span className={styles.value}>{userInfo?.whenCreated || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Last Modified</span>
								<span className={styles.value}>{userInfo?.whenChanged || '-'}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.label}>Last Logon</span>
								<span className={styles.value}>{userInfo?.lastLogon || '-'}</span>
							</div>

							<div className={`${styles.infoRow} ${styles.fullRow}`}>
								<span className={styles.label}>Distinguished Name</span>
								<span className={styles.value}>{userInfo?.distinguishedName || '-'}</span>
							</div>

							<div className={`${styles.infoRow} ${styles.fullRow}`}>
								<span className={styles.label}>Status</span>
								<span className={styles.value}>
									<Tag color={userInfo?.active ? 'success' : 'error'}>
										{userInfo?.active ? 'Active' : 'Inactive'}
									</Tag>
									<Tag color={userInfo?.locked ? 'error' : 'success'}>
										{userInfo?.locked ? 'Locked' : 'Unlocked'}
									</Tag>
									<Tag color={userInfo?.passwordExpired ? 'error' : 'success'}>
										{userInfo?.passwordExpired ? 'Password Expired' : 'Password Valid'}
									</Tag>
									<Tag color={userInfo?.passwordMustChange ? 'warning' : 'success'}>
										{userInfo?.passwordMustChange ? 'Must Change Password' : 'No Force Change'}
									</Tag>
								</span>
							</div>

							<div className={`${styles.infoRow} ${styles.fullRow}`}>
								<span className={styles.label}>Message</span>
								<span className={styles.value}>{userInfo?.message || '-'}</span>
							</div>
						</div>
					) : (
						<div className={styles.notFoundMsg}>
							{userInfo?.message || 'User not found in AD'}
						</div>
					)}
				</Card>
			)}
		</div>
	);
};

export default ADPasswordReset;